import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

const NAV_ITEMS = [
  { label: "숙소 리스트", href: "#best15" },
  { label: "호스트 스토리", href: "#host-story" },
  { label: "예약 가이드", href: "#booking-guide" },
  { label: "이용 후기", href: "#trust" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // 홈(/)이 아닌 페이지에서는 항상 흰 배경 고정
  const isHome = location.pathname === "/";
  const isTransparent = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    // 페이지 진입 시 즉시 체크
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent ? "bg-transparent" : "bg-white/95 backdrop-blur-md shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <a href="#" className="flex items-center gap-2.5 cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/97140e49-74bc-47a8-9097-71ae89eff5f5.png"
            alt="우리민박 로고"
            className="h-10 w-auto object-contain"
          />
          <span className={`text-base font-bold tracking-tight transition-colors ${
            isTransparent ? "text-white" : "text-stone-800"
          }`}>
            우리민박
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.href}
              onClick={() => handleNavClick(item.href)}
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                isTransparent ? "text-white/90 hover:text-white" : "text-stone-700 hover:text-amber-600"
              }`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* SNS + CTA */}
        <div className="hidden md:flex items-center gap-3">
          {/* 카카오톡 */}
          <a
            href="#"
            title="카카오톡 문의"
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${
              isTransparent
                ? "bg-white/20 hover:bg-[#FEE500] text-white hover:text-[#3A1D1D]"
                : "bg-[#FEE500] hover:bg-[#f0d800] text-[#3A1D1D]"
            }`}
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/e/e3/KakaoTalk_logo.svg"
              alt="카카오톡"
              className="w-5 h-5 object-contain"
            />
          </a>

          {/* 라인 */}
          <a
            href="#"
            title="라인 문의"
            className={`w-9 h-9 flex items-center justify-center rounded-full transition-all cursor-pointer ${
              isTransparent
                ? "bg-white/20 hover:bg-[#06C755] text-white"
                : "bg-[#06C755] hover:bg-[#05b34c] text-white"
            }`}
          >
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/4/41/LINE_logo.svg"
              alt="라인"
              className="w-5 h-5 object-contain"
            />
          </a>

          {/* CTA Button */}
          <a
            href="#best15"
            onClick={(e) => { e.preventDefault(); handleNavClick("#best15"); }}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-heart-line text-base"></i>
            숙소 보러가기
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${isTransparent ? "text-white" : "text-stone-800"}`}
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 px-6 py-4 flex flex-col gap-4">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.href}
              onClick={() => handleNavClick(item.href)}
              className="text-left text-sm font-medium text-stone-700 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {item.label}
            </button>
          ))}
          <a
            href="#best15"
            onClick={(e) => { e.preventDefault(); handleNavClick("#best15"); }}
            className="inline-flex items-center justify-center gap-2 bg-amber-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full whitespace-nowrap cursor-pointer"
          >
            숙소 보러가기
          </a>

          {/* 모바일 SNS 버튼 */}
          <div className="flex items-center gap-3 pt-1">
            <a
              href="#"
              title="카카오톡 문의"
              className="flex items-center gap-2 bg-[#FEE500] text-[#3A1D1D] text-sm font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/e/e3/KakaoTalk_logo.svg"
                alt="카카오톡"
                className="w-4 h-4 object-contain"
              />
              카카오톡 문의
            </a>
            <a
              href="#"
              title="라인 문의"
              className="flex items-center gap-2 bg-[#06C755] text-white text-sm font-semibold px-4 py-2 rounded-full whitespace-nowrap cursor-pointer"
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/4/41/LINE_logo.svg"
                alt="라인"
                className="w-4 h-4 object-contain"
              />
              라인 문의
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
